/**
 * TodoRow — 나의 할일 '오늘' 줄(6칸: 현장·신호 점·공종·제목·사유·처리). 배너·D-1·슬라이드 공용.
 * 신호 점은 SignalDot(D-07 §1.2) — 점 옆에 제목이 항상 있다. 할일 코드는 보이지 않는다(서버 제목만).
 * 줄 클릭은 todo.rowHref(viewPath), 처리 버튼은 서버가 준 동작(webPath 이동 · actionUrl 호출). 주소가 없으면 줄은 클릭되지 않고 버튼은 그리지 않는다.
 * 알림 이후 생긴 할일은 'NEW'. 클릭 행은 role="button"·tabIndex·Enter/Space(D-09 §15.28).
 * 화면 이동이 없는 처리 버튼(API)은 누르면 처리 중(loading)으로 바뀌고, 성공하면 버튼 자리에 '✓ {처리 이름} 완료 HH:MM' 배지를 둔다.
 * 실패하면 버튼으로 돌아온다(오류 안내는 useTodoNavigate 토스트). 완료 표시는 이 화면에 있는 동안만 유지된다(새로 열면 서버 상태대로).
 */
import { useState, type KeyboardEvent } from 'react';
import { Badge, Button, Tooltip } from '@indipia/ui-core';
import { SignalDot } from './SignalDot';
import { hasAction, type TodoItem } from './todoModel';
import type { TodoNavTarget } from './useTodoNavigate';

const PHONE_DISABLED_TIP = '웹에서는 전화연결이 어려워요.';

/** 전화 말풍선 — 웹은 바로 걸 수 없으니 서버가 준 반장님 이름 · 번호를 같은 줄에 함께 보여 준다(표준 Tooltip 은 줄바꿈 없음). */
function phoneTip(phone: { name?: string | null; telText?: string | null; tel: string }): string {
  const who = [phone.name, phone.telText ?? phone.tel].filter(Boolean).join(' ');
  return who ? `${PHONE_DISABLED_TIP} ${who}` : PHONE_DISABLED_TIP;
}

export const PhoneIcon = ({ size = 14 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const CheckIcon = ({ size = 12 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** 버튼 끝 '›' — 화면 이동 표시. Figma 버튼 아이콘(PT8wG7aRcWg4CerPViHOI4 / 4390:2795) 16 프레임, 글자와 간격은 .btn gap 이 준다. */
export const ChevronRightIcon = ({ size = 16 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const pad2 = (n: number) => String(n).padStart(2, '0');

/** 처리 버튼 결과 — 이동(ROUTE)은 결과가 없고, API 는 성공 여부를 Promise 로 돌려준다. */
type ActionResult = Promise<boolean> | void;

// 줄 6칸 — 현장명 · 신호 점 · 공종 · 제목(NEW) · 사유 · 처리(전화·버튼 — 이동 버튼은 글자 뒤 ›)
const ROW_GRID =
  'grid grid-cols-[minmax(130px,1.1fr)_10px_minmax(90px,.8fr)_minmax(140px,1.2fr)_minmax(0,2fr)_200px] items-center gap-x-3.5 px-5 py-3';

export interface TodoRowProps {
  todo: TodoItem;
  /** 같은 현장 묶음의 첫 줄이면 true(현장명 표시) */
  showSite: boolean;
  /** 담당자 필터가 전체일 때만 true — 묶음 첫 줄에 '담당 X' 표기 */
  showOwner?: boolean;
  /** 앞칸에 현장명 대신 보일 글자 — 현장별 보기(카드 머리가 현장)에서 '지금 처리' · '오늘 일정' */
  lead?: string;
  /** 이동 — target 이 row 면 rowHref 가 있는 줄, action 이면 처리 버튼이 있는 줄에서만 불린다 */
  onNavigate?: (todo: TodoItem, target: TodoNavTarget) => ActionResult;
}

export function TodoRow({ todo, showSite, showOwner = true, lead, onNavigate }: TodoRowProps) {
  const red = todo.level === 'r';
  const linked = todo.rowHref != null;
  const actionLinked = hasAction(todo);
  const [pending, setPending] = useState(false);
  const [doneAt, setDoneAt] = useState<Date | null>(null);
  const open = () => { if (linked) onNavigate?.(todo, 'row'); };
  const runAction = () => {
    const result = onNavigate?.(todo, 'action');
    if (!result) return; // 화면 이동(ROUTE)
    setPending(true);
    result.then((ok) => {
      setPending(false);
      if (ok) setDoneAt(new Date());
    });
  };
  const doneLabel = `${todo.action} 완료`;
  // 버튼 안 '›'는 버튼이 화면을 옮길 때만(ROUTE href) — API 호출(actionUrl)은 화면 이동이 아니라 붙이지 않는다.
  // 줄 클릭 이동(rowHref)은 줄 hover · 커서 · 말풍선(destLabel)이 알린다(버튼 밖 '›'는 버튼 이동으로 오인돼 없앴다).
  const actionMoves = todo.actionType !== 'API' && todo.href != null;
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return; // 안쪽 버튼의 키 입력은 줄 클릭으로 번지지 않게
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  };
  return (
    <div
      {...(linked ? { role: 'button', tabIndex: 0, onClick: open, onKeyDown } : {})}
      title={todo.destLabel || undefined}
      className={`${ROW_GRID} ${linked ? 'cursor-pointer transition-colors hover:bg-group-subtle' : ''} ${
        showSite ? 'border-t border-border first:border-t-0' : 'border-t border-dashed border-border'
      }`}
    >
      <div className="flex min-w-0 flex-col">
        {showSite && lead != null && <span className="truncate text-body-sm font-bold text-text-secondary">{lead}</span>}
        {showSite && lead == null && (
          <>
            <span className="truncate text-body-sm font-bold text-text" title={todo.site}>{todo.site}</span>
            {showOwner && todo.owner && <span className="text-caption text-text-muted">담당 {todo.owner}</span>}
          </>
        )}
      </div>
      <SignalDot color={red ? 'red' : 'yellow'} />
      <span className="truncate text-body-sm text-text-secondary">{todo.task}</span>
      <span className="flex min-w-0 items-center gap-1.5">
        <span className="truncate text-body-sm font-bold text-text">{todo.title}</span>
        {todo.isNew && <Badge status="accent">NEW</Badge>}
      </span>
      <span className="truncate text-body-sm text-text-muted" title={todo.reason}>{todo.reason}</span>
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        {todo.phone && (
          <Tooltip tip={phoneTip(todo.phone)}>
            <Button variant="secondary" size="sm" disabled aria-label={todo.phone.label}><PhoneIcon /></Button>
          </Tooltip>
        )}
        {actionLinked && doneAt == null && (
          <Button variant="secondary" size="sm" loading={pending} disabled={pending} onClick={runAction}>
            {todo.action}
            {actionMoves && <ChevronRightIcon />}
          </Button>
        )}
        {actionLinked && doneAt != null && (
          <Badge status="success" role="status" title={doneLabel} className="inline-flex min-w-0 items-center gap-1">
            <CheckIcon />
            <span className="truncate">{doneLabel}</span>
            <span className="shrink-0">{pad2(doneAt.getHours())}:{pad2(doneAt.getMinutes())}</span>
          </Badge>
        )}
      </div>
    </div>
  );
}

/** 같은 현장이 이어지면 현장명을 생략한다(묶음 첫 줄만 표시). */
export function TodoRows({
  items, prevSite = '', showOwner = true, onNavigate,
}: {
  items: TodoItem[];
  prevSite?: string;
  showOwner?: boolean;
  onNavigate?: (todo: TodoItem, target: TodoNavTarget) => ActionResult;
}) {
  let prev = prevSite;
  return (
    <div>
      {items.map((t) => {
        const first = t.site !== prev;
        prev = t.site;
        return <TodoRow key={t.id} todo={t} showSite={first} showOwner={showOwner} onNavigate={onNavigate} />;
      })}
    </div>
  );
}
